import { useQuery } from "@tanstack/react-query";
import { API_BASE_URL } from "@/backend/config";

// Types matching the admin config
export type RatioBarConfig = {
  enabled: boolean;
  gap_px: number;
  height_px: number;
  transition_ms: number;
};

export type SentimentConfig = {
  enabled: boolean;
  slow_wave_amplitude: number;
  slow_wave_period_ms: number;
  medium_wave_amplitude: number;
  medium_wave_period_ms: number;
  fast_wave_amplitude: number;
  fast_wave_period_ms: number;
  burst_threshold: number;
  burst_multiplier: number;
  noise_amplitude: number;
  update_interval_ms: number;
};

export type GenerationConfig = {
  enabled: boolean;
  depth_curve_factor: number;
  base_variation_min: number;
  base_variation_max: number;
  fast_wave_amplitude: number;
  fast_wave_period_ms: number;
  medium_wave_amplitude: number;
  medium_wave_period_ms: number;
  slow_wave_amplitude: number;
  slow_wave_period_ms: number;
  jitter_amplitude: number;
  round_number_bonus_major: number;
  round_number_bonus_minor: number;
  animation_interval_ms: number; // How often order book regenerates (external clock)
};

export type OrderBookConfig = {
  ratio_bar: RatioBarConfig;
  sentiment: SentimentConfig;
  generation: GenerationConfig;
};

// Default values (fallback if API fails)
export const DEFAULT_ORDER_BOOK_CONFIG: OrderBookConfig = {
  ratio_bar: {
    enabled: true,
    gap_px: 4,
    height_px: 20,
    transition_ms: 150,
  },
  sentiment: {
    enabled: true,
    slow_wave_amplitude: 12,
    slow_wave_period_ms: 8000,
    medium_wave_amplitude: 8,
    medium_wave_period_ms: 5000,
    fast_wave_amplitude: 6,
    fast_wave_period_ms: 1200,
    burst_threshold: 0.9,
    burst_multiplier: 30,
    noise_amplitude: 2,
    update_interval_ms: 150,
  },
  generation: {
    enabled: true,
    depth_curve_factor: 0.12,
    base_variation_min: 0.9,
    base_variation_max: 1.45,
    fast_wave_amplitude: 0.12,
    fast_wave_period_ms: 500,
    medium_wave_amplitude: 0.08,
    medium_wave_period_ms: 2000,
    slow_wave_amplitude: 0.05,
    slow_wave_period_ms: 6000,
    jitter_amplitude: 0.06,
    round_number_bonus_major: 3.5,
    round_number_bonus_minor: 1.5,
    animation_interval_ms: 150, // Default: professional speed matching major exchanges
  },
};

type SiteSettingResponse = {
  setting: {
    key: string;
    value: OrderBookConfig;
    updated_at: string;
    updated_by?: string;
  };
};

async function fetchOrderBookConfig(): Promise<OrderBookConfig> {
  try {
    const res = await fetch(
      `${API_BASE_URL}/api/site-settings/order_book_config`
    );
    if (!res.ok) {
      console.warn("Failed to fetch order book config, using defaults");
      return DEFAULT_ORDER_BOOK_CONFIG;
    }
    const data: SiteSettingResponse = await res.json();
    return data.setting.value;
  } catch (error) {
    console.warn("Error fetching order book config:", error);
    return DEFAULT_ORDER_BOOK_CONFIG;
  }
}

/**
 * Hook to fetch order book config settings
 * Falls back to defaults if API fails (in fetchOrderBookConfig)
 * 
 * CRITICAL: No placeholderData to prevent config switching instability
 */
export function useOrderBookConfig() {
  return useQuery({
    queryKey: ["site-settings", "order_book_config"],
    queryFn: fetchOrderBookConfig,
    staleTime: 60_000, // Cache for 1 minute
    refetchInterval: 5 * 60_000, // Refresh every 5 minutes
    // Removed placeholderData - was causing constant switching between code default and DB value
    // This created unstable object references triggering excessive re-renders
  });
}

